"use client";

import Image from "next/image";
import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

export type DepthTile = {
  id: string;
  title: string;
  category: string;
  description: string;
  imageSrc?: string;
};

type Props = {
  tiles: DepthTile[];
  /** Délai entre deux rotations automatiques (ms) */
  intervalMs?: number;
};

/**
 * "Depth Tiles Infinite Loop" — carrousel en profondeur :
 * la tuile active est au centre, les voisines glissent derrière sur les côtés,
 * la boucle est infinie (auto-rotation + clic sur une tuile latérale).
 * Le descriptif de la tuile active s'affiche sous le carrousel.
 */
export default function DepthTilesLoop({ tiles, intervalMs = 3800 }: Props) {
  const [active, setActive] = useState(0);
  const n = tiles.length;
  const hovering = useRef(false);

  // Rotation automatique — suspendue au survol pour laisser lire
  useEffect(() => {
    if (n < 2) return;
    const t = setInterval(() => {
      if (!hovering.current) setActive((a) => (a + 1) % n);
    }, intervalMs);
    return () => clearInterval(t);
  }, [n, intervalMs]);

  const goTo = useCallback((i: number) => setActive(i), []);

  if (n === 0) return null;

  // Position relative de chaque tuile par rapport à l'active
  const slot = (i: number): "center" | "right" | "left" | "hidden" => {
    const rel = (i - active + n) % n;
    if (rel === 0) return "center";
    if (rel === 1) return "right";
    if (rel === n - 1 && n > 2) return "left";
    return "hidden";
  };

  const styleFor = (s: ReturnType<typeof slot>): React.CSSProperties => {
    switch (s) {
      case "center":
        return {
          transform: "translateX(-50%) scale(1)",
          zIndex: 30,
          opacity: 1,
          filter: "brightness(1)",
        };
      case "right":
        return {
          transform: "translateX(calc(-50% + 42%)) scale(0.8)",
          zIndex: 20,
          opacity: 0.65,
          filter: "brightness(0.55)",
        };
      case "left":
        return {
          transform: "translateX(calc(-50% - 42%)) scale(0.8)",
          zIndex: 20,
          opacity: 0.65,
          filter: "brightness(0.55)",
        };
      default:
        return {
          transform: "translateX(-50%) scale(0.7)",
          zIndex: 10,
          opacity: 0,
          filter: "brightness(0.4)",
        };
    }
  };

  const activeTile = tiles[active];

  return (
    <div
      onMouseEnter={() => (hovering.current = true)}
      onMouseLeave={() => (hovering.current = false)}
    >
      {/* Scène du carrousel */}
      <div className="relative h-52 w-full overflow-hidden">
        {tiles.map((tile, i) => {
          const s = slot(i);
          const isCenter = s === "center";
          return (
            <button
              key={tile.id}
              type="button"
              onClick={() => !isCenter && goTo(i)}
              aria-label={tile.title}
              aria-current={isCenter}
              className={[
                "absolute left-1/2 top-0 h-full w-[72%] overflow-hidden rounded-2xl border border-white/10",
                "transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]",
                isCenter ? "cursor-default" : "cursor-pointer",
              ].join(" ")}
              style={styleFor(s)}
              tabIndex={isCenter ? -1 : 0}
            >
              {tile.imageSrc ? (
                <Image
                  src={tile.imageSrc}
                  alt={tile.title}
                  fill
                  className="object-cover"
                  sizes="320px"
                />
              ) : (
                /* Placeholder en attendant l'image dédiée */
                <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-white/12 via-white/[0.05] to-transparent">
                  <span className="text-[9px] tracking-[0.3em] uppercase text-white/25 font-body">
                    {tile.category}
                  </span>
                </div>
              )}

              {/* Voile bas pour la lisibilité du label */}
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/70 to-transparent" />

              {/* Label bas-gauche — « • 01 / Nom » */}
              <span className="absolute bottom-3 left-4 flex items-center gap-2 text-left">
                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-white" />
                <span className="text-[12px] font-body text-white">
                  {String(i + 1).padStart(2, "0")} / {tile.title}
                </span>
              </span>
            </button>
          );
        })}
      </div>

      {/* Descriptif de la tuile active */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTile.id}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          className="mt-4"
        >
          <div className="flex items-baseline justify-between gap-3">
            <h5 className="font-heading text-lg font-light text-white">
              {activeTile.title}
            </h5>
            <span className="whitespace-nowrap text-[9px] tracking-[0.28em] uppercase text-white/40 font-body">
              {activeTile.category}
            </span>
          </div>
          <p className="mt-1.5 text-sm leading-relaxed text-white/55 font-body font-light">
            {activeTile.description}
          </p>
        </motion.div>
      </AnimatePresence>

      {/* Points de navigation */}
      {n > 1 && (
        <div className="mt-3 flex justify-center gap-1.5">
          {tiles.map((t, i) => (
            <button
              key={t.id}
              type="button"
              onClick={() => goTo(i)}
              aria-label={t.title}
              className={[
                "h-1 rounded-full transition-all duration-300",
                i === active ? "w-5 bg-white/80" : "w-1.5 bg-white/25 hover:bg-white/50",
              ].join(" ")}
            />
          ))}
        </div>
      )}
    </div>
  );
}
